import React, { useState, useRef, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { Sun, Moon } from 'lucide-react'
import { useTheme } from '../context/ThemeContext'

const MODE_OPTIONS = [
  { id: 'light', label: 'Light', Icon: Sun },
  { id: 'dark', label: 'Dark', Icon: Moon },
]

/**
 * Swatches mirroring the three dark ramps in index.css. They are duplicated here
 * rather than read from CSS variables because each card has to render its *own*
 * intensity, not the one currently applied to the page.
 */
const STYLE_OPTIONS = [
  {
    id: 'enhanced',
    label: 'Enhanced',
    hint: 'Highest contrast. Best on OLED screens.',
    canvas: '#000000',
    surface: '#1A1A1A',
    border: '#454545',
    bar: '#FFFFFF',
  },
  {
    id: 'medium',
    label: 'Medium',
    hint: 'Balanced contrast. Recommended.',
    canvas: '#0D0D0D',
    surface: '#1F1F1F',
    border: '#2E2E2E',
    bar: '#EDEDED',
  },
  {
    id: 'gentle',
    label: 'Gentle',
    hint: 'Softest contrast, easier for long sessions.',
    canvas: '#1A1A1A',
    surface: '#2A2A2A',
    border: '#3D3D3D',
    bar: '#DCDCDC',
  },
]

const DOT_COLORS = ['#4ADE80', '#60A5FA', '#FB923C']

/** The mini UI mock shown inside each intensity card. */
function StylePreview({ option, selected }) {
  return (
    <div
      className="w-full aspect-square rounded-xl p-2.5 flex flex-col justify-center gap-2 transition-shadow"
      style={{
        backgroundColor: option.surface,
        border: `1px solid ${selected ? 'transparent' : option.border}`,
        boxShadow: selected ? 'none' : `inset 0 0 0 1px ${option.border}`,
      }}
    >
      {DOT_COLORS.map((dot) => (
        <div key={dot} className="flex items-center gap-2">
          <span
            className="rounded-full flex-shrink-0"
            style={{ backgroundColor: dot, width: 9, height: 9 }}
          />
          <span
            className="rounded-full flex-1"
            style={{ backgroundColor: option.bar, height: 5, opacity: 0.92 }}
          />
        </div>
      ))}
    </div>
  )
}

export default function ThemePopover() {
  const { isDark, mode, setMode, darkStyle, setDarkStyle } = useTheme()
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState(null)

  const triggerRef = useRef(null)
  const panelRef = useRef(null)

  const place = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect()
    if (!rect) return
    const PANEL_WIDTH = 320
    setPosition({
      top: rect.bottom + 8,
      // Right-align to the trigger, but never let the panel leave the viewport.
      left: Math.max(8, Math.min(rect.right - PANEL_WIDTH, window.innerWidth - PANEL_WIDTH - 8)),
      width: PANEL_WIDTH,
    })
  }, [])

  useEffect(() => {
    if (!open) return

    place()

    const onPointerDown = (event) => {
      if (panelRef.current?.contains(event.target)) return
      if (triggerRef.current?.contains(event.target)) return
      setOpen(false)
    }
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        setOpen(false)
        triggerRef.current?.focus()
      }
    }

    document.addEventListener('mousedown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    window.addEventListener('resize', place)
    window.addEventListener('scroll', place, true)
    return () => {
      document.removeEventListener('mousedown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
      window.removeEventListener('resize', place)
      window.removeEventListener('scroll', place, true)
    }
  }, [open, place])

  // Closing the popover BEFORE the reveal starts matters: the panel is 320px
  // wide and sits right on top of the 20px trigger icon, so if it stayed open
  // it would visually swallow the first ~150px of the circle's growth — the
  // reveal would still technically start at the icon, but with the panel
  // covering that whole area it would look like nothing was happening there.
  // Closing first gives an unobstructed view of the page the circle grows over.
  const selectMode = (id) => {
    setOpen(false)
    setMode(id, triggerRef.current)
  }
  const selectStyle = (id) => {
    setOpen(false)
    setDarkStyle(id, triggerRef.current)
  }

  // Arrow-key navigation within the intensity radio group, per WAI-ARIA.
  const onStyleKeyDown = (event) => {
    const keys = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }
    const step = keys[event.key]
    if (!step) return
    event.preventDefault()
    const index = STYLE_OPTIONS.findIndex((option) => option.id === darkStyle)
    const next = STYLE_OPTIONS[(index + step + STYLE_OPTIONS.length) % STYLE_OPTIONS.length]
    selectStyle(next.id)
  }

  const ModeIcon = isDark ? Sun : Moon

  return (
    <>
      <button
        ref={triggerRef}
        onClick={() => setOpen((value) => !value)}
        className="p-2 rounded-lg text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        title="Appearance"
        aria-label="Appearance settings"
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <ModeIcon className="h-5 w-5" />
      </button>

      {open && position && createPortal(
        <div
          ref={panelRef}
          role="dialog"
          aria-label="Appearance"
          className="fixed z-[9999] rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl p-4 animate-fade-in"
          style={{ top: position.top, left: position.left, width: position.width }}
        >
          <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
            Appearance
          </p>

          <div
            role="radiogroup"
            aria-label="Colour mode"
            className="grid grid-cols-2 gap-1.5 p-1 rounded-lg bg-gray-100 dark:bg-gray-800"
          >
            {MODE_OPTIONS.map(({ id, label, Icon }) => {
              const selected = mode === id
              return (
                <button
                  key={id}
                  role="radio"
                  aria-checked={selected}
                  onClick={() => selectMode(id)}
                  className={`flex flex-col items-center gap-1 py-2 rounded-md text-xs font-medium transition-colors ${
                    selected
                      ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 shadow-sm'
                      : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              )
            })}
          </div>

          {/* Only relevant once dark mode is active — hidden rather than shown
              disabled while in light mode, since it has no effect there. */}
          {isDark && (
            <div className="mt-4 pt-3 border-t border-gray-100 dark:border-gray-800">
              <p className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-2">
                Style
              </p>

              <div
                role="radiogroup"
                aria-label="Dark mode contrast"
                onKeyDown={onStyleKeyDown}
                className="grid grid-cols-3 gap-2"
              >
                {STYLE_OPTIONS.map((option) => {
                  const selected = darkStyle === option.id
                  return (
                    <button
                      key={option.id}
                      data-style-id={option.id}
                      role="radio"
                      aria-checked={selected}
                      tabIndex={selected ? 0 : -1}
                      title={option.hint}
                      onClick={() => selectStyle(option.id)}
                      className="group flex flex-col items-center gap-2 rounded-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500"
                    >
                      <div
                        className="w-full rounded-xl p-1 transition-colors"
                        style={{
                          backgroundColor: option.canvas,
                          outline: selected ? '2px solid rgb(var(--navy-400))' : '2px solid transparent',
                          outlineOffset: 2,
                        }}
                      >
                        <StylePreview option={option} selected={selected} />
                      </div>

                      <span
                        className={`text-xs font-medium ${
                          selected
                            ? 'text-navy-600 dark:text-navy-300'
                            : 'text-gray-600 dark:text-gray-400'
                        }`}
                      >
                        {option.label}
                      </span>

                      <span
                        aria-hidden="true"
                        className={`h-4 w-4 rounded-full border-2 flex items-center justify-center transition-colors ${
                          selected
                            ? 'border-navy-500 dark:border-navy-400'
                            : 'border-gray-300 dark:border-gray-600'
                        }`}
                      >
                        {selected && (
                          <span className="h-2 w-2 rounded-full bg-navy-500 dark:bg-navy-300" />
                        )}
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>
          )}
        </div>,
        document.body
      )}
    </>
  )
}
